import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  Company,
  CompanyBody,
  CompanyInvoices,
  CompanyPlans,
  Role,
  MessageObject,
  User,
  EmailObjectWithMandatoryEmail,
} from "@vviinn/user-managment-client-ts";

import { FetchableState } from "..";
import { serializeModel, ApiRequest } from "../../../api";
import { MandatoryCompanyId, CompanyAllPlansRequest } from "../../../company";

type CompanyState = FetchableState & {
  errorMessage?: string;
  company?: Company;
};

const initialState: CompanyState = {
  error: false,
  loading: false,
  status: "idle",
};

export const updateCompany = createAsyncThunk(
  "company/update",
  async ({ api, value }: ApiRequest<CompanyBody>) => {
    const response = await api.upsertCompany(value);

    return serializeModel<Company>(response.value);
  },
);

export const getCompany = createAsyncThunk(
  "company/get",
  async ({ api, value }: ApiRequest<MandatoryCompanyId>) => {
    const response = await api.getCompany(value);

    return serializeModel<Company>(response.value);
  },
);

export const getCompanyAllPlans = createAsyncThunk(
  "company/getAllPlans",
  async ({ api, value }: ApiRequest<CompanyAllPlansRequest>) => {
    const response = await api.getCompanyAllPlans(
      value.companyId,
      value.action,
    );

    return serializeModel<CompanyPlans>(response.value);
  },
);

export const deleteCompany = createAsyncThunk(
  "company/delete",
  async ({ api, value }: ApiRequest<MandatoryCompanyId>) => {
    const response = await api.deleteCompany(value);

    return serializeModel<MessageObject>(response.value);
  },
);

export type AssignCompanyRoleRequestBody = {
  companyId: MandatoryCompanyId;
  userId: User["uid"];
  role: Role;
};

export const assignUser = createAsyncThunk(
  "company/assignUser",
  async ({ api, value }: ApiRequest<AssignCompanyRoleRequestBody>) => {
    const { companyId, userId, role } = value;
    await api.addCompanyViewerRoleToUser(companyId, userId.toString(), role);
    return true;
  },
);

export const unassignUser = createAsyncThunk(
  "company/assignUser",
  async ({ api, value }: ApiRequest<AssignCompanyRoleRequestBody>) => {
    const { companyId, userId, role } = value;
    await api.removeCompanyViewerRoleToUser(companyId, userId, role);
    return true;
  },
);

export type InviteUserRequestBody = {
  companyId: MandatoryCompanyId;
  email: EmailObjectWithMandatoryEmail["email"];
};

export const inviteUserToCompany = createAsyncThunk(
  "company/inviteUser",
  async ({ api, value }: ApiRequest<InviteUserRequestBody>) => {
    const { companyId, email } = value;
    const response = await api.inviteUserToCompany(companyId, { email });
    return serializeModel<MessageObject>(response.value);
  },
);

export const getCompanyInvoices = createAsyncThunk(
  "company/getInvoices",

  async ({ api, value }: ApiRequest<MandatoryCompanyId>) => {
    const response = await api.getCompanyInvoices(value);
    return serializeModel<CompanyInvoices>(response.value);
  },
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    removeCompanyMemberById(state, action) {
      const removedUserId = action.payload;
      if (!state.company?.members) return;
      if (state.company.owners)
        state.company.owners = state.company.owners.filter(
          (owner) => owner.id !== removedUserId,
        );
      state.company.members = state.company.members.filter(
        (member) => member.id !== removedUserId,
      );
    },
    changeCompanyStatus(state, { payload }) {
      state.status = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateCompany.fulfilled, (state, { payload }) => {
      state.company = payload;
      state.error = false;
      state.errorMessage = undefined;
    });
    builder.addCase(updateCompany.rejected, (state, { error }) => {
      state.error = true;
      state.errorMessage = error.message;
    });
    builder.addCase(getCompany.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = undefined;
      state.status = "loading";
    });
    builder.addCase(getCompany.fulfilled, (state, { payload }) => {
      const oldId = state.company?.id;
      const oldName = state.company?.name;
      state.company = payload;
      if (oldId && oldName && oldId === payload.id && !payload.name.length)
        //FIXME: backend issue https://projects.mediaopt.de/issues/35450 is back
        state.company.name = oldName;
      state.loading = false;
      state.error = false;
      state.errorMessage = undefined;
      state.status = "fulfilled";
    });
    builder.addCase(getCompany.rejected, (state, { error }) => {
      state.company = undefined;
      state.loading = false;
      state.error = true;
      state.errorMessage = error.message;
      state.status = "error";
    });
    builder.addCase(deleteCompany.fulfilled, (state, { payload }) => {
      if (payload.message === "success" && state.company) {
        state.company.id = "";
        state.company.name = "Select a company";
      }
    });
    builder.addCase(getCompanyAllPlans.fulfilled, (state, { payload }) => {
      if (payload.items && state.company) {
        state.company.plans = { ...payload };
      }
    });
  },
});

export default companySlice.reducer;
export const { removeCompanyMemberById, changeCompanyStatus } =
  companySlice.actions;
